import { Box } from '@mui/material'
import { usePlanningStore } from '@planning/AppProvider'
import { InitializationWrapper } from '@planning/components'
import { useTranslate } from '@tolgee/react'
import { Breadcrumbs, Loader, PageHeader, useMinimalsTheme } from '@tom-ui/ui'
import { PATHS, useAsyncFetch } from '@tom-ui/utils'
import { observer } from 'mobx-react-lite'
import { FC, useMemo } from 'react'
import { Helmet } from 'react-helmet-async'
import { useNavigate, useParams } from 'react-router'
import { RenderIsoCodeMappingInputFn } from '../../../Order/stores/IsoCodeMappingInputStore'
import { NonNumericReleaseForm } from '../../Components/organisms/NonNumericReleases/NonNumericReleaseForm'
import { NnrOrderControlViewStore, NnrOrderFormData } from '../../Stores/NnrOrderControlViewStore'
import { useReleaseMessagesHandler } from '../../hooks/useReleaseMessagesHandler'

interface Props {
  renderIsoCodeMappingInput?: RenderIsoCodeMappingInputFn
}

export const NonNumericReleaseFormControlPage: FC<Props> = observer(
  ({ renderIsoCodeMappingInput }) => {
    const { t } = useTranslate()
    const { appViewStore, nnrOrderItemStore, isoCodeMappingInputStore } = usePlanningStore()
    const navigate = useNavigate()
    const theme = useMinimalsTheme()
    const { getNonNumericReleaseSuccessMessage, getExceptionMessage } = useReleaseMessagesHandler()

    isoCodeMappingInputStore.renderIsoCodeMappingInput = renderIsoCodeMappingInput

    const nnrOrderControlViewStore = useMemo(
      () => new NnrOrderControlViewStore(nnrOrderItemStore),
      [nnrOrderItemStore],
    )

    const editLabel = t('editNonNumericRelease', 'Edit non-numeric release')
    const createLabel = t('createNonNumericRelease', 'Create non-numeric release')

    const { nnrOrderId } = useParams()

    const title = nnrOrderId ? editLabel : createLabel

    const operator = nnrOrderControlViewStore.selectedNnrOrder?.operator?.data.shortName
    const referenceNumber = nnrOrderControlViewStore.selectedNnrOrder?.data.referenceNumber

    const identifier = operator ? `${operator}-${referenceNumber}` : referenceNumber

    const breadcrumbs = [
      {
        label: t('nonNumericReleases', 'Non-numeric releases'),
        onClick: () => navigate(`${PATHS.orders.nonNumericReleases.url}`),
      },
      {
        label: nnrOrderId
          ? `${t('editNnrBreadcrumb', 'Edit {identifier}', { identifier })}`
          : createLabel,
      },
    ]

    const { loading } = useAsyncFetch(async () => {
      const id = Number(nnrOrderId)

      if (id > 0) {
        nnrOrderControlViewStore.setId(id)
        await nnrOrderItemStore.fetchById(id)
      }
    }, [nnrOrderId, nnrOrderControlViewStore, nnrOrderItemStore])

    const onFormSubmit = async (data: NnrOrderFormData) => {
      if (data.containerIsoCode && (!data.archetypeId || data.archetypeId === -1)) {
        appViewStore.setShowAlert(
          'error',
          t('isoCodeDoesNotMatchAnyArchetype', 'ISO Code does not match any archetype'),
        )
        return
      }

      try {
        nnrOrderControlViewStore.setIsLoading(true)
        await nnrOrderControlViewStore.onSubmit(data)
        appViewStore.setShowAlert(
          'success',
          getNonNumericReleaseSuccessMessage(nnrOrderControlViewStore.isEditMode),
        )
        navigate(`${PATHS.orders.nonNumericReleases.url}`)
      } catch (error) {
        appViewStore.setShowAlert('error', getExceptionMessage(error))
      } finally {
        nnrOrderControlViewStore.setIsLoading(false)
      }
    }

    return (
      <InitializationWrapper isInitializing={loading}>
        <>
          <Loader show={nnrOrderControlViewStore.isLoading} />
          <Helmet>
            <title>{title}</title>
          </Helmet>
          <Breadcrumbs items={breadcrumbs} />
          <PageHeader
            pageTitle={title}
            formId='nnr-order-form'
            onBack={() => navigate(`${PATHS.orders.nonNumericReleases.url}`)}
          />
          <Box
            paddingX={theme.customSpacing.l}
            paddingY={theme.customSpacing.m}
            gap={theme.customSpacing.m}
            display={'flex'}
            flexDirection={'column'}
          >
            <NonNumericReleaseForm viewStore={nnrOrderControlViewStore} onSubmit={onFormSubmit} />
          </Box>
        </>
      </InitializationWrapper>
    )
  },
)
